import type { JSX } from 'react';
import type { PaymentIntent, PaymentIntentLineItem } from '../types/api';
import type { Locale } from '../i18n';
import { formatAmount } from '../money/formatAmount';
import { BrandMark } from './BrandMark';
import { widgetText } from './copy';
import {
  AMOUNT_CLASS,
  AMOUNT_PANEL_CLASS,
  MUTED_TEXT_CLASS,
  STACK_CLASS,
  SURFACE_CLASS,
} from './styles';

export interface PaymentSummaryProps {
  intent: PaymentIntent;
  locale: Locale;
}

/** El logo del tenant solo se acepta por HTTPS (plan 7.3.5). */
function httpsLogoUrl(candidate: string | undefined): string | null {
  if (candidate === undefined || candidate === '') {
    return null;
  }
  try {
    return new URL(candidate).protocol === 'https:' ? candidate : null;
  } catch {
    return null;
  }
}

/** `expires_at` puede llegar vacio: payment-api aun no lo expone en todos los casos. */
function formatExpiry(isoInstant: string, locale: Locale): string | null {
  if (isoInstant.length === 0) {
    return null;
  }
  const parsed = new Date(isoInstant);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
}

/**
 * Resumen no interactivo del intent: comercio, monto, descripcion y vencimiento de la
 * sesion. No renderiza ningun campo de entrada: la captura ocurre siempre dentro del
 * componente alojado de la pasarela (PCI DSS SAQ A, plan 7.1.1).
 */
/**
 * A partir de cuantas lineas el desglose deja de crecer y se desplaza.
 *
 * <p>El API acepta hasta {@code LineItem.MAX_ITEMS} = 50 lineas. Cincuenta filas son unos 2.000px
 * de tarjeta: en modo pagina eso estira el panel entero y manda la publicidad --y el pie-- a un
 * sitio al que nadie baja. Seis es lo que cabe holgado en la columna de 20rem sin que el importe
 * deje de verse a la vez que el detalle.
 */
export const LINEAS_SIN_SCROLL = 6;

/**
 * Desglose de lo que se paga, debajo del importe.
 *
 * <p>Se pinta como una LISTA DE DESCRIPCION y no como una tabla: son pares concepto-importe, no una
 * matriz de datos, y un lector de pantalla los lee en pareja sin tener que anunciar filas y
 * columnas.
 *
 * <p>El total de cada linea es el que manda el servidor, no el producto calculado aqui: el mismo
 * numero que se valido contra el importe del cobro. Y la linea de cantidad solo aparece cuando hay
 * mas de una unidad, porque «1 x $12.990» junto a «$12.990» es ruido.
 *
 * <p>NO se repite el total al final. Ya esta arriba, con el peso tipografico que le corresponde;
 * repetirlo invitaria a sumar mentalmente para comprobar si cuadra, y que cuadre es responsabilidad
 * del servidor, que rechaza al crear el cobro cualquier desglose que no sume.
 */
function Desglose({
  items,
  currency,
  locale,
}: {
  items: readonly PaymentIntentLineItem[];
  currency: string;
  locale: Locale;
}): JSX.Element {
  /*
   * El tope vale en TODOS los anchos, tambien en movil.
   *
   * La primera version lo dejaba en `lg` con el argumento de que una zona de scroll anidada en
   * tactil es incomoda. Lo es, pero el problema que resuelve es peor: apilado, el resumen va ENCIMA
   * del selector, asi que doce lineas empujan los medios de pago fuera de pantalla y el comprador
   * tiene que desplazarse un buen rato para encontrar el boton por el que ha entrado. Con cincuenta
   * --el tope del API-- directamente no lo encuentra.
   */
  const largo = items.length > LINEAS_SIN_SCROLL;

  return (
    <div data-testid="payment-breakdown" className="flex flex-col gap-2">
      <p className="flex items-baseline justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--apipay-color-muted,#64748b)]">
        <span>{widgetText(locale, 'summary.breakdown')}</span>
        {/*
          El recuento aparece SOLO cuando hay scroll, y es lo que impide que recortar sea esconder:
          quien ve seis lineas de doce tiene que enterarse de que hay doce sin descubrirlo
          arrastrando.
        */}
        {largo ? (
          <span data-testid="breakdown-count" className="font-normal normal-case tracking-normal">
            {widgetText(locale, 'summary.item_count', { count: items.length })}
          </span>
        ) : null}
      </p>
      <dl
        // `tabIndex` solo cuando hay algo que desplazar: una zona de scroll a la que no se puede
        // llegar con el teclado deja las lineas de abajo fuera del alcance de quien no usa raton, y
        // una parada de tabulacion en una lista que cabe entera es ruido.
        {...(largo
          ? { tabIndex: 0, 'aria-label': widgetText(locale, 'summary.breakdown_scroll') }
          : {})}
        // `pr-1` deja sitio a la barra para que no se monte sobre los importes de la derecha.
        className={'flex flex-col gap-1.5' + (largo ? ' max-h-64 overflow-y-auto pr-1' : '')}
      >
        {items.map((item, index) => (
          <div
            // El indice entra en la clave porque dos lineas pueden llamarse igual --dos talles del
            // mismo producto en dos lineas-- y el nombre solo no las distingue.
            key={`${index}-${item.name}`}
            data-testid="breakdown-item"
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <dt className="min-w-0">
              <span className="block break-words">{item.name}</span>
              {item.quantity > 1 ? (
                <span className={`block ${MUTED_TEXT_CLASS}`}>
                  {widgetText(locale, 'summary.units', {
                    quantity: item.quantity,
                    unit: formatAmount(item.unit_price_minor, currency, locale),
                  })}
                </span>
              ) : null}
            </dt>
            <dd
              className="shrink-0 tabular-nums"
              data-amount-minor={String(item.total_price_minor)}
            >
              {formatAmount(item.total_price_minor, currency, locale)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function PaymentSummary({ intent, locale }: PaymentSummaryProps): JSX.Element {
  const { checkout } = intent;
  const items = intent.line_items ?? [];
  const desglose =
    items.length === 0 ? null : (
      <Desglose items={items} currency={intent.currency} locale={locale} />
    );
  const logoUrl = httpsLogoUrl(checkout.logo_url ?? checkout.theme?.logo_url);
  const expiry = formatExpiry(intent.expires_at, locale);
  const merchantName =
    checkout.tenant_name === undefined || checkout.tenant_name === '' ? null : checkout.tenant_name;

  return (
    <section
      data-testid="payment-summary"
      aria-label={widgetText(locale, 'summary.title')}
      className={`${SURFACE_CLASS} ${STACK_CLASS}`}
    >
      {/*
        Identidad del comercio en una fila: su marca y su nombre. Antes el nombre iba como una linea
        gris mas, igual que el vencimiento; aqui es lo primero que ve el comprador, que es lo que le
        dice a quien le esta pagando.
      */}
      {merchantName === null && logoUrl === null ? null : (
        <div className="flex items-center gap-3">
          <BrandMark
            name={merchantName ?? '?'}
            logoUrl={logoUrl ?? undefined}
            shape="circle"
            size={40}
          />
          {merchantName === null ? null : (
            <span className="min-w-0">
              <span className="block text-xs uppercase tracking-wide text-[var(--apipay-color-muted,#64748b)]">
                {widgetText(locale, 'summary.merchant')}
              </span>
              <span className="block truncate text-sm font-semibold">{merchantName}</span>
            </span>
          )}
        </div>
      )}

      {/*
        El importe es el dato por el que se entra a esta pantalla: se le da el peso que merece, y
        ahora tambien un panel tenido con el color del comercio para que la vista caiga ahi antes
        de bajar al selector.
      */}
      <div className={`${AMOUNT_PANEL_CLASS} flex flex-col gap-0.5`}>
        <p className={MUTED_TEXT_CLASS}>{widgetText(locale, 'summary.total')}</p>
        <p
          className={AMOUNT_CLASS}
          data-amount-minor={String(intent.amount_minor)}
          data-currency={intent.currency}
        >
          {formatAmount(intent.amount_minor, intent.currency, locale)}
        </p>
        {intent.description === undefined || intent.description === '' ? null : (
          <p className="text-sm text-[var(--apipay-color-text,#0f172a)]">{intent.description}</p>
        )}
      </div>

      {desglose}

      {expiry === null ? null : (
        <p className={MUTED_TEXT_CLASS}>{widgetText(locale, 'summary.expires_at', { date: expiry })}</p>
      )}
    </section>
  );
}
